import ignore from 'ignore';

import {
  ANALYSIS_SCHEMA_VERSION,
  type AnalysisReport,
  type AnalysisWarning,
  type AnalyzeProjectInput,
  type GitignoreSourceInput,
  type MatchState,
  type MatchedRule,
  type NormalizedProjectPath,
  type ParsedGitignoreSource,
  type ParsedRule,
  type PathAnalysis,
  type PathInputIssue,
  type RuleAnalysis,
  type RuleReference,
  type RuleStatus,
} from '../../types';
import { parseGitignoreSource } from '../gitignore/parser';
import {
  isPathInsideDirectory,
  normalizeProjectPaths,
  parentDirectories,
  relativeToDirectory,
} from '../path';

type IgnoreInstance = ReturnType<typeof ignore>;

const createIgnore = (): IgnoreInstance => ignore({ ignorecase: false });

interface RuntimeSource {
  readonly parsed: ParsedGitignoreSource;
  readonly rulesById: ReadonlyMap<string, ParsedRule>;
}

interface NodeState {
  readonly ignored: boolean;
  readonly decisiveRule: ParsedRule | null;
}

interface InternalMatch {
  readonly rule: ParsedRule;
  readonly stateAfter: MatchState;
  readonly blocked: boolean;
  readonly blockedParents: readonly string[];
}

interface InternalPathAnalysis {
  readonly input: NormalizedProjectPath;
  readonly ignored: boolean;
  readonly decisiveRule: ParsedRule | null;
  readonly matches: readonly InternalMatch[];
}

interface MutableRuleStats {
  matchCount: number;
  decisiveCount: number;
  blockedCount: number;
  readonly affectedPaths: Set<string>;
}

interface BlockedOccurrence {
  readonly path: string;
  readonly parents: readonly string[];
}

interface IgnoreCheck {
  readonly ignored: boolean;
  readonly unignored: boolean;
  readonly rule?: {
    readonly pattern: string;
    readonly negative: boolean;
    readonly mark?: string;
  };
}

const BROAD_PATTERNS = new Set(['*', '**', '/*', '/**', '**/*', '**/']);

export function analyzeProject(input: AnalyzeProjectInput): AnalysisReport {
  // Pattern editors can invoke analysis repeatedly; keep the per-run matcher
  // cache bounded so stale drafts do not accumulate in a long-lived worker.
  ruleMatcherCache.clear();
  const generatedAt = normalizedDate(input.now);
  const normalizedPaths = normalizeProjectPaths(input.paths);
  const prepared = prepareSources(input.sources);
  const inputIssues = [...normalizedPaths.issues, ...prepared.issues];
  const runtimeSources = prepared.sources.map<RuntimeSource>((parsed) => ({
    parsed,
    rulesById: new Map(parsed.rules.map((rule) => [rule.id, rule])),
  }));
  const allRules = prepared.sources.flatMap((source) => source.rules);
  const stats = new Map<string, MutableRuleStats>(
    allRules.map((rule) => [
      rule.id,
      { matchCount: 0, decisiveCount: 0, blockedCount: 0, affectedPaths: new Set() },
    ]),
  );
  const blockedByRule = new Map<string, BlockedOccurrence[]>();

  const internalPaths = normalizedPaths.paths.map((path) => evaluatePath(path, runtimeSources));

  for (const result of internalPaths) {
    for (const match of result.matches) {
      const ruleStats = stats.get(match.rule.id);
      if (ruleStats === undefined) {
        continue;
      }
      ruleStats.matchCount += 1;
      ruleStats.affectedPaths.add(result.input.path);
      if (match.blocked) {
        ruleStats.blockedCount += 1;
        const occurrences = blockedByRule.get(match.rule.id) ?? [];
        occurrences.push({ path: result.input.path, parents: match.blockedParents });
        blockedByRule.set(match.rule.id, occurrences);
      }
    }

    if (result.decisiveRule !== null) {
      const ruleStats = stats.get(result.decisiveRule.id);
      if (ruleStats !== undefined) {
        ruleStats.decisiveCount += 1;
      }
    }
  }

  const warnings: AnalysisWarning[] = [];
  for (const rule of allRules) {
    if (isBroadRule(rule)) {
      const affectedPaths = [...(stats.get(rule.id)?.affectedPaths ?? [])].sort(compareStrings);
      warnings.push({
        id: `broad-rule:${rule.id}`,
        code: 'broad-rule',
        severity: 'info',
        message: `Pattern \`${rule.raw}\` can match a very large part of the project.`,
        reason:
          'Broad rules are valid, but a later edit can make them hide more paths than intended.',
        paths: affectedPaths,
        source: rule.source,
        line: rule.line,
        suggestedRule: null,
        suggestedSource: null,
      });
    }

    const blocked = blockedByRule.get(rule.id);
    if (blocked !== undefined && blocked.length > 0) {
      const uniquePaths = [...new Set(blocked.map((item) => item.path))].sort(compareStrings);
      const parents = uniqueBlockedParents(blocked);
      warnings.push({
        id: `blocked-negation:${rule.id}`,
        code: 'blocked-negation',
        severity: 'warning',
        message: `Negation \`${rule.raw}\` cannot re-include its targets while a parent directory is ignored.`,
        reason:
          'Git does not traverse an ignored directory, so each ignored parent must be re-included before a child rule can take effect.',
        paths: uniquePaths,
        source: rule.source,
        line: rule.line,
        suggestedRule: suggestedParentNegations(rule, parents),
        suggestedSource: rule.source,
      });
    }
  }

  for (const result of internalPaths) {
    if (result.input.kind !== 'file' || !isSensitivePath(result.input.path)) {
      continue;
    }

    if (!result.ignored) {
      const wasReincluded = result.decisiveRule?.negated === true;
      warnings.push({
        id: `${wasReincluded ? 'sensitive-reincluded' : 'sensitive-unignored'}:${result.input.path}`,
        code: wasReincluded ? 'sensitive-path-reincluded' : 'sensitive-path-unignored',
        severity: 'warning',
        message: wasReincluded
          ? `Sensitive-looking path \`${result.input.path}\` is re-included.`
          : `Sensitive-looking path \`${result.input.path}\` is not ignored.`,
        reason:
          'This warning is based only on the filename. Verify that secrets, credentials, and private keys cannot be committed.',
        paths: [result.input.path],
        source: result.decisiveRule?.source ?? null,
        line: result.decisiveRule?.line ?? null,
        suggestedRule: suggestedSensitiveRule(result.input.path),
        suggestedSource: preferredSuggestionSource(prepared.sources),
      });
    }
  }

  warnings.sort(compareWarnings);
  const warningsByPath = indexWarningsByPath(warnings);
  const paths = internalPaths.map<PathAnalysis>((result) => {
    const decisiveRule = result.decisiveRule === null ? null : toRuleReference(result.decisiveRule);
    const matchedRules = result.matches.map<MatchedRule>((match) => ({
      ...toRuleReference(match.rule),
      stateAfter: match.stateAfter,
      decisive: match.rule.id === result.decisiveRule?.id,
      blocked: match.blocked,
      explanation: matchExplanation(match),
    }));
    const pathWarnings = warningsByPath.get(result.input.path) ?? [];

    return {
      path: result.input.path,
      kind: result.input.kind,
      inferred: result.input.inferred,
      ignored: result.ignored,
      decisiveRule,
      matchedRules,
      ruleSource: decisiveRule?.source ?? null,
      ruleLine: decisiveRule?.line ?? null,
      explanation: pathExplanation(result),
      warnings: pathWarnings,
    };
  });

  const rules = allRules.map<RuleAnalysis>((rule) => buildRuleAnalysis(rule, stats.get(rule.id)));
  const ignoredCount = paths.filter((path) => path.ignored).length;
  const explicitPathCount = paths.filter((path) => !path.inferred).length;

  return {
    schemaVersion: ANALYSIS_SCHEMA_VERSION,
    generatedAt,
    summary: {
      pathCount: paths.length,
      explicitPathCount,
      inferredPathCount: paths.length - explicitPathCount,
      ignoredCount,
      trackedCount: paths.length - ignoredCount,
      warningCount: warnings.length,
      sourceCount: prepared.sources.length,
      ruleCount: rules.length,
    },
    sources: prepared.sources.map((source) => ({
      path: source.path,
      directory: source.directory,
      ruleIds: source.rules.map((rule) => rule.id),
    })),
    rules,
    paths,
    warnings,
    inputIssues,
  };
}

function evaluatePath(
  input: NormalizedProjectPath,
  sources: readonly RuntimeSource[],
): InternalPathAnalysis {
  const nodePaths = [...parentDirectories(input.path), input.path];
  let states = nodePaths.map<NodeState>(() => ({ ignored: false, decisiveRule: null }));
  const matches: InternalMatch[] = [];

  for (const source of sources) {
    if (!isPathInsideDirectory(input.path, source.parsed.directory)) {
      continue;
    }

    if (!sourceCanBeRead(source.parsed.directory, nodePaths, states)) {
      continue;
    }

    const baseStates = states;
    const matcher = createIgnore();
    const appliedRules: ParsedRule[] = [];

    for (const rule of source.parsed.rules) {
      if (rule.kind !== 'pattern') {
        continue;
      }

      if (!addRule(matcher, rule)) {
        continue;
      }
      appliedRules.push(rule);
      states = evaluateNodesForSource(source, matcher, appliedRules, nodePaths, baseStates);

      const targetRelative = relativeToDirectory(input.path, source.parsed.directory);
      if (targetRelative === null || targetRelative.length === 0) {
        continue;
      }

      const targetState = states.at(-1);
      if (targetState === undefined) {
        continue;
      }
      const candidate =
        ruleMatchesRelative(rule, targetRelative) || targetState.decisiveRule?.id === rule.id;
      if (!candidate) {
        continue;
      }

      const blockedParents = rule.negated
        ? nodePaths.slice(0, -1).filter((_, index) => states[index]?.ignored === true)
        : [];
      matches.push({
        rule,
        stateAfter: targetState.ignored ? 'ignored' : 'tracked',
        blocked: rule.negated && blockedParents.length > 0,
        blockedParents,
      });
    }
  }

  const targetState = states.at(-1) ?? { ignored: false, decisiveRule: null };
  return {
    input,
    ignored: targetState.ignored,
    decisiveRule: targetState.decisiveRule,
    matches,
  };
}

function evaluateNodesForSource(
  source: RuntimeSource,
  matcher: IgnoreInstance,
  appliedRules: readonly ParsedRule[],
  nodePaths: readonly string[],
  baseStates: readonly NodeState[],
): NodeState[] {
  const next: NodeState[] = [];

  for (const [index, nodePath] of nodePaths.entries()) {
    const base = baseStates[index] ?? { ignored: false, decisiveRule: null };
    const relative = relativeToDirectory(nodePath, source.parsed.directory);

    if (relative === null || relative.length === 0) {
      next.push(base);
      continue;
    }

    const parent = index === 0 ? null : next[index - 1];
    if (parent?.ignored === true) {
      next.push({ ignored: true, decisiveRule: parent.decisiveRule });
      continue;
    }

    const checked = safeCheckIgnore(matcher, relative);
    if (checked.ignored) {
      next.push({
        ignored: true,
        decisiveRule: resolveCheckedRule(checked, appliedRules, source.rulesById, relative, false),
      });
      continue;
    }

    if (checked.unignored) {
      next.push({
        ignored: false,
        decisiveRule: resolveCheckedRule(checked, appliedRules, source.rulesById, relative, true),
      });
      continue;
    }

    next.push(base);
  }

  return next;
}

function sourceCanBeRead(
  directory: string,
  nodePaths: readonly string[],
  states: readonly NodeState[],
): boolean {
  if (directory.length === 0) {
    return true;
  }
  const directoryPath = `${directory}/`;
  const index = nodePaths.indexOf(directoryPath);
  return index === -1 || states[index]?.ignored !== true;
}

function addRule(matcher: IgnoreInstance, rule: ParsedRule): boolean {
  try {
    matcher.add({ pattern: rule.pattern, mark: rule.id });
    return true;
  } catch {
    return false;
  }
}

function safeCheckIgnore(matcher: IgnoreInstance, path: string): IgnoreCheck {
  try {
    return matcher.checkIgnore(path);
  } catch {
    return { ignored: false, unignored: false };
  }
}

function resolveCheckedRule(
  checked: IgnoreCheck,
  appliedRules: readonly ParsedRule[],
  rulesById: ReadonlyMap<string, ParsedRule>,
  relativePath: string,
  wantNegated: boolean,
): ParsedRule | null {
  // `checkIgnore()` deliberately short-circuits some already-ignored paths and
  // can retain the first positive mark. Git attribution uses last-match wins, so
  // resolve the displayed decisive rule from our ordered, source-local list.
  for (let index = appliedRules.length - 1; index >= 0; index -= 1) {
    const rule = appliedRules[index];
    if (rule?.negated === wantNegated && ruleMatchesRelative(rule, relativePath)) {
      return rule;
    }
  }

  const marked = checked.rule?.mark;
  if (marked !== undefined) {
    return rulesById.get(marked) ?? null;
  }
  return null;
}

const ruleMatcherCache = new Map<string, IgnoreInstance | null>();

function ruleMatchesRelative(rule: ParsedRule, relativePath: string): boolean {
  const cacheKey = `${rule.id}\0${rule.pattern}`;
  let matcher = ruleMatcherCache.get(cacheKey);
  if (matcher === undefined) {
    try {
      matcher = createIgnore().add(positiveEquivalent(rule));
    } catch {
      matcher = null;
    }
    ruleMatcherCache.set(cacheKey, matcher);
  }
  return matcher === null ? false : safeCheckIgnore(matcher, relativePath).ignored;
}

function positiveEquivalent(rule: ParsedRule): string {
  if (!rule.negated) {
    return rule.pattern;
  }
  const body = rule.pattern.slice(1);
  if (body.startsWith('#') || body.startsWith('!')) {
    return `\\${body}`;
  }
  return body;
}

function prepareSources(inputs: readonly GitignoreSourceInput[]): {
  readonly sources: readonly ParsedGitignoreSource[];
  readonly issues: readonly PathInputIssue[];
} {
  const sources = new Map<string, ParsedGitignoreSource>();
  const issues: PathInputIssue[] = [];

  for (const input of inputs) {
    try {
      const source = parseGitignoreSource(input);
      if (sources.has(source.path)) {
        issues.push({
          code: 'duplicate-source',
          input: input.path,
          message: `Duplicate ignore source \`${source.path}\` was analyzed once.`,
        });
        continue;
      }
      sources.set(source.path, source);
    } catch (error: unknown) {
      issues.push({
        code: 'invalid-source-path',
        input: input.path,
        message: error instanceof Error ? error.message : 'The ignore source could not be parsed.',
      });
    }
  }

  return {
    sources: [...sources.values()].sort((left, right) => {
      const depthDifference = pathDepth(left.directory) - pathDepth(right.directory);
      return depthDifference === 0 ? compareStrings(left.path, right.path) : depthDifference;
    }),
    issues,
  };
}

function buildRuleAnalysis(rule: ParsedRule, stats?: MutableRuleStats): RuleAnalysis {
  const matchCount = stats?.matchCount ?? 0;
  const decisiveCount = stats?.decisiveCount ?? 0;
  const blockedCount = stats?.blockedCount ?? 0;
  const broad = isBroadRule(rule);
  const lifecycle = lifecycleStatus(rule, matchCount, decisiveCount, blockedCount);
  const status: RuleStatus =
    broad && rule.kind === 'pattern' && lifecycle !== 'Blocked negation' ? 'Broad rule' : lifecycle;
  const statuses = new Set<RuleStatus>([status, lifecycle]);
  if (matchCount > 0) {
    statuses.add('Matched');
  }
  if (broad) {
    statuses.add('Broad rule');
  }

  return {
    ...rule,
    status,
    statuses: [...statuses],
    matchCount,
    decisiveCount,
    blockedCount,
    affectedPaths: [...(stats?.affectedPaths ?? [])].sort(compareStrings),
  };
}

function lifecycleStatus(
  rule: ParsedRule,
  matchCount: number,
  decisiveCount: number,
  blockedCount: number,
): RuleStatus {
  if (rule.kind === 'blank') {
    return 'Blank';
  }
  if (rule.kind === 'comment') {
    return 'Comment';
  }
  if (rule.negated && blockedCount > 0 && decisiveCount === 0) {
    return 'Blocked negation';
  }
  if (decisiveCount > 0) {
    return 'Active';
  }
  if (matchCount === 0) {
    return 'Unmatched';
  }
  return 'Overridden';
}

function isBroadRule(rule: ParsedRule): boolean {
  if (rule.kind !== 'pattern' || rule.negated) {
    return false;
  }
  return BROAD_PATTERNS.has(effectivePattern(rule.raw));
}

function effectivePattern(raw: string): string {
  let end = raw.length;
  while (end > 0 && raw[end - 1] === ' ') {
    let backslashes = 0;
    for (let index = end - 2; index >= 0 && raw[index] === '\\'; index -= 1) {
      backslashes += 1;
    }
    if (backslashes % 2 === 1) {
      break;
    }
    end -= 1;
  }
  return raw.slice(0, end);
}

function isSensitivePath(path: string): boolean {
  const basename = path.split('/').at(-1)?.toLowerCase() ?? '';
  if (basename === '.env.example' || basename === '.env.sample' || basename === '.env.template') {
    return false;
  }
  return (
    basename === '.env' ||
    basename.startsWith('.env.') ||
    basename.endsWith('.pem') ||
    basename.endsWith('.key') ||
    basename === 'id_rsa' ||
    basename === 'id_ed25519' ||
    basename === 'credentials.json' ||
    basename === 'service-account.json' ||
    basename === 'service_account.json'
  );
}

function suggestedSensitiveRule(path: string): string {
  const basename = path.split('/').at(-1)?.toLowerCase() ?? path;
  if (basename === '.env' || basename.startsWith('.env.')) {
    return '.env*';
  }
  if (basename.endsWith('.pem')) {
    return '*.pem';
  }
  if (basename.endsWith('.key')) {
    return '*.key';
  }
  return basename;
}

function preferredSuggestionSource(sources: readonly ParsedGitignoreSource[]): string {
  return sources.find((source) => source.path === '.gitignore')?.path ?? '.gitignore';
}

function suggestedParentNegations(rule: ParsedRule, parents: readonly string[]): string | null {
  const relativeParents = parents
    .map((parent) => relativeToDirectory(parent, rule.sourceDirectory))
    .filter((parent): parent is string => parent !== null && parent.length > 0);
  const unique = [...new Set(relativeParents)];
  return unique.length === 0 ? null : unique.map((parent) => `!${parent}`).join('\n');
}

function uniqueBlockedParents(occurrences: readonly BlockedOccurrence[]): readonly string[] {
  const parents = new Set<string>();
  for (const occurrence of occurrences) {
    for (const parent of occurrence.parents) {
      parents.add(parent);
    }
  }
  return [...parents].sort(
    (left, right) => pathDepth(left) - pathDepth(right) || compareStrings(left, right),
  );
}

function pathExplanation(result: InternalPathAnalysis): string {
  const lastBlocked = [...result.matches].reverse().find((match) => match.blocked);
  if (result.ignored && lastBlocked !== undefined) {
    return `Ignored because ${lastBlocked.blockedParents.join(', ')} must be re-included before negation \`${lastBlocked.rule.raw}\` can restore this path.`;
  }
  if (result.decisiveRule === null) {
    return result.ignored
      ? 'Ignored because an ancestor directory is ignored.'
      : 'Tracked because no applicable ignore rule matched this path.';
  }
  if (result.ignored) {
    return `Ignored by \`${result.decisiveRule.raw}\` in ${result.decisiveRule.source}:${String(result.decisiveRule.line)}.`;
  }
  return `Tracked because \`${result.decisiveRule.raw}\` in ${result.decisiveRule.source}:${String(result.decisiveRule.line)} re-included it.`;
}

function matchExplanation(match: InternalMatch): string {
  if (match.blocked) {
    return `The negation matched, but ${match.blockedParents.join(', ')} remained ignored.`;
  }
  if (match.rule.negated) {
    return match.stateAfter === 'tracked'
      ? 'After this negation, the path is tracked.'
      : 'This negation did not change the path because another rule still applies.';
  }
  return match.stateAfter === 'ignored'
    ? 'After this rule, the path is ignored.'
    : 'This rule matched, but a later decision kept the path tracked.';
}

function toRuleReference(rule: ParsedRule): RuleReference {
  return {
    id: rule.id,
    source: rule.source,
    line: rule.line,
    raw: rule.raw,
    pattern: rule.pattern,
    negated: rule.negated,
  };
}

function indexWarningsByPath(
  warnings: readonly AnalysisWarning[],
): ReadonlyMap<string, readonly AnalysisWarning[]> {
  const index = new Map<string, AnalysisWarning[]>();
  for (const warning of warnings) {
    for (const path of warning.paths) {
      const current = index.get(path) ?? [];
      current.push(warning);
      index.set(path, current);
    }
  }
  return index;
}

function normalizedDate(value: AnalyzeProjectInput['now']): string {
  const date =
    value instanceof Date
      ? new Date(value.getTime())
      : value === undefined
        ? new Date()
        : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('`now` must be a valid date value.');
  }
  return date.toISOString();
}

function pathDepth(path: string): number {
  if (path.length === 0) {
    return 0;
  }
  return path.split('/').filter(Boolean).length;
}

function compareStrings(left: string, right: string): number {
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}

function compareWarnings(left: AnalysisWarning, right: AnalysisWarning): number {
  const sourceComparison = compareStrings(left.source ?? '', right.source ?? '');
  if (sourceComparison !== 0) {
    return sourceComparison;
  }
  const lineComparison =
    (left.line ?? Number.MAX_SAFE_INTEGER) - (right.line ?? Number.MAX_SAFE_INTEGER);
  return lineComparison === 0 ? compareStrings(left.id, right.id) : lineComparison;
}
